import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { ActionControlId, MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import type { RibbonControlId } from 'owa-ribbon-ids';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import * as ControlDef from './mlrHomeTabControlDefinitions';
import * as SharedControlDef from '../../sharedControls/readSharedControlDefinitions';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getMLRHomeTabControlDefinition = computedFn(
    (controlId: RibbonControlId, props: RibbonControlDefinitionProps): RibbonControlProps => {
        switch (controlId) {
            case ActionControlId.GetAddIns:
                return ControlDef.getAddInsControl();
            case MailRibbonControlId.AddressBook:
                return ControlDef.getAddressBookControl();
            case MailRibbonControlId.Archive:
                return ControlDef.getArchiveControl();
            case MailRibbonControlId.AssignPolicy:
                return SharedControlDef.getAssignPolicyControl(
                    false /*isSLR*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/
                );
            case MailRibbonControlId.BrowseGroups:
                return ControlDef.getDiscoverGroupsControl();
            case MailRibbonControlId.Categorize:
                return ControlDef.getCategorizeControl();
            case MailRibbonControlId.DeleteItem:
                return SharedControlDef.getDeleteControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/
                );
            case MailRibbonControlId.FlagUnflag:
                return SharedControlDef.getFlagUnflagControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/
                );
            case MailRibbonControlId.IgnoreStopIgnore:
                return ControlDef.getIgnoreControl();
            case MailRibbonControlId.Insights:
                return ControlDef.getInsightsControl();
            case MailRibbonControlId.JunkFlyout:
                return SharedControlDef.getJunkControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.Move:
                return ControlDef.getMoveControl();
            case MailRibbonControlId.NewMessage:
                return ControlDef.getNewMessageControl(MailRibbonTabId.Tab_Home);
            case MailRibbonControlId.Phishing:
                return SharedControlDef.getPhishingControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.PinUnpin:
                return ControlDef.getPinUnpinControl();
            case MailRibbonControlId.QuickStepsGallery:
                return SharedControlDef.getQuickStepsControl(false /*isSLR*/);
            case MailRibbonControlId.ReadAloud:
                return ControlDef.getReadAloudControl();
            case MailRibbonControlId.ReadUnread:
                return SharedControlDef.getUnreadReadControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/
                );
            case MailRibbonControlId.ReplyWithMeeting:
                return SharedControlDef.getReplyWithMeetingControl(
                    false /*isSLR*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/
                );
            case MailRibbonControlId.ReportMessage:
                return ControlDef.getReportMessageControl();
            case MailRibbonControlId.PlayMyEmails:
                return SharedControlDef.getPlayMyEmailsControl(MailRibbonTabId.Tab_Home);
            case MailRibbonControlId.RespondForward:
                return SharedControlDef.getForwardControl(
                    false /*isSLR*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.RespondReply:
                return SharedControlDef.getReplyControl(
                    false /*isSLR*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.RespondReplyAll:
                return SharedControlDef.getReplyAllControl(
                    false /*isSLR*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.Rules:
                return SharedControlDef.getRulesControl(
                    false /*isSLR*/,
                    false /*useIconColor*/,
                    MailRibbonTabId.Tab_Home,
                    undefined /*itemId*/,
                    undefined /*targetWindow*/
                );
            case MailRibbonControlId.Snooze:
                return ControlDef.getSnoozeControl();
            case MailRibbonControlId.Sweep:
                return ControlDef.getSweepControl();
            case MailRibbonControlId.ToggleLeftPane:
                return ControlDef.getLeftToggleControl();
            case MailRibbonControlId.Undo:
                return ControlDef.getUndoControl();
            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getMLRHomeTabControlDefinition'
                );
        }
    }
);
